import React from 'react'
import Pt from 'prop-types'
import { Table } from 'react-bootstrap'

import Currency from 'internal-components/src/components/Currency'
import Months from 'internal-components/src/components/Time/Months'
import UpdatingAgo from 'internal-components/src/components/Time/UpdatingAgo'
import StateLabel from 'internal-components/src/components/Signup/StateLabel'
import { SignupWithExternalsRecord } from 'internal-components/src/records'

import StateDescription from './StateDescription'
import PaymentDetails from '../Payment/Details'

import './DetailTable.css'

function empty(val) { return val === '0' || !val; }

export default function DetailTable({ signupWithExternals, expectedCoupon }) {
  const transitionByState = {};
  signupWithExternals.signup.get('stateTransitions').forEach(transition => {
    let state = transition.get('toState');
    if (!transitionByState.hasOwnProperty(state)) {
      transitionByState[state] = transition;
    }
  });

  const initialDiscount = expectedCoupon ? expectedCoupon.discount : 0;

  let install
  if (signupWithExternals.signup.state === 'pending-install') {
    const transition = signupWithExternals.signup.stateTransitions.first()

    install = (
      <tr className="warning">
        <th>Scheduled Installation</th>
        <td>
          <p>
            The installation for your service is scheduled for{' '}
            <strong>{transition.installDate.format('D MMM YYYYDD')}</strong>
            {transition.installTime && (
              <span>
                {' '}
                with an estimated time of{' '}
                <strong>{transition.installTime}</strong>
              </span>
            )}
            .
          </p>

          <p>
            If you cannot make this scheduled appointment date or time, please
            contact us immediately on <tel>1300 790 337</tel>.
          </p>
        </td>
      </tr>
    )
  }

  const hasModem = (
    !empty(signupWithExternals.signup.data.get('nbn.modem')) ||
    !empty(signupWithExternals.signup.data.get('nbn.nf18mesh.v1')) ||
    !empty(signupWithExternals.signup.data.get('nbn.nl19mesh.v1')) ||
    !empty(signupWithExternals.signup.data.get('nbn.nl19mesh.v2'))
  );

  const pricedByExtensions = signupWithExternals.offer.attributes.has('price_by_extensions');

  let priceMultiplier = 1, phoneExtensionCount;
  if (pricedByExtensions) {
    phoneExtensionCount = priceMultiplier = signupWithExternals.signup.data.get('phone-ext-count') || 1;
  }

  let skipContract = false;
  signupWithExternals.signup.data.forEach((v, k) => {
    let opt = signupWithExternals.product.options.filter(option => option.dataKey === k).first()
    if (opt && parseFloat(v) && opt.skipContract) {
      skipContract = true;
    }
  });

  // Note: this must be kept in sync with SignupPdfController in invocation.
  return (
    <Table className="signup-detail-table">
      <tbody>
        <tr>
          <th>Service</th>
          <td>{signupWithExternals.offer.get('name')}</td>
        </tr>
        <tr>
          <th>Reference</th>
          <td>{signupWithExternals.signup.reference}</td>
        </tr>
        <tr>
          <th>Status</th>
          <td>
            <StateDescription state={signupWithExternals.signup.state} />
          </td>
        </tr>
        {signupWithExternals.signup.commitmentDate && <tr>
          <th>Commitment Date</th>
          <td>
            {signupWithExternals.signup.commitmentDate.format('D/M/YYYY')}<br />
            (<UpdatingAgo Component="small" moment={signupWithExternals.signup.commitmentDate} />)
          </td>
        </tr>}
        {install}
        <tr>
          <th>Address</th>
          <td>
            {signupWithExternals.signup.get('address') || 'No physical address'}
          </td>
        </tr>
        {!!phoneExtensionCount && <tr>
          <th>Phone Extensions</th>
          <td>{phoneExtensionCount}</td>
        </tr>}
        <tr>
          <th>Setup Fee</th>
          <td>
            <Currency amount={signupWithExternals.signup.get('setupAmount') * priceMultiplier} />
          </td>
        </tr>
        <tr>
          <th>Recurring Fee</th>
          <td>
            {expectedCoupon ?
              <div>
                <Currency amount={signupWithExternals.signup.get('recurringAmount') * priceMultiplier} /> per{' '}
                <Months dropOne duration={signupWithExternals.offer.get('period')} />{' '}
                <br />
                <Currency amount={(signupWithExternals.signup.get('recurringAmount') - initialDiscount) * priceMultiplier} /> for first {expectedCoupon.months} months
              </div> :
              <div>
                <Currency
                  amount={signupWithExternals.signup.get('recurringAmount') * priceMultiplier}
                />{' '}
                per{' '}
                <Months
                  dropOne
                  duration={signupWithExternals.offer.get('period')}
                />
                {signupWithExternals.signup.coupon &&
                  <div>
                    <br/>
                    Coupon applied: <strong>{signupWithExternals.signup.coupon}</strong>
                    {signupWithExternals.signup.coupon.effectiveFor && <span>{' '}for <Months duration={signupWithExternals.signup.coupon.effectiveFor}/></span>}
                  </div>}
              </div>}
          </td>
        </tr>
        <tr>
          <th>First Payment</th>
          <td>
            <Currency amount={signupWithExternals.signup.get('firstPayment') - initialDiscount * priceMultiplier} />
            <br />
            <small className="text-muted">
              First{' '}
              <Months
                dropOne
                duration={signupWithExternals.offer.get('period')}
              />{' '}
              + setup
              {hasModem ? ' + modem' : ''}
              {empty(signupWithExternals.signup.data.get('voip.phone_handset')) ? '' : ' + phone handset'}
              {empty(signupWithExternals.signup.data.get('voip.existing_phone_number')) ? '' : ' + existing phone number'}
            </small>
          </td>
        </tr>
        {transitionByState.paid && transitionByState.paid.getIn(['additionalCharges', 'nrc']) && <tr className="warning">
          <th>Subsequent Installation Fee</th>
          <td><Currency amount={transitionByState.paid.getIn(['additionalCharges', 'nrc'])} /></td>
        </tr>}
        {transitionByState.paid && transitionByState.paid.getIn(['additionalCharges', 'ndc']) && <tr className="warning">
          <th>New Development Charge</th>
          <td><Currency amount={transitionByState.paid.getIn(['additionalCharges', 'ndc'])} /></td>
        </tr>}
        <tr>
          <th>Contract Duration</th>
          <td>
            {signupWithExternals.offer.get('contractLength') && !skipContract && (
              <div>
                <Months
                  duration={signupWithExternals.offer.get('contractLength')}
                />
                <br />
                <small className="text-muted">
                  {' '}
                  Minimum Spend{' '}
                  <Currency
                    amount={signupWithExternals.signup.get('minimumSpend')}
                  />
                </small>
              </div>
            )}
            {(!signupWithExternals.offer.get('contractLength') || skipContract) && (
              <small className="text-muted">No Contract</small>
            )}
          </td>
        </tr>
        {signupWithExternals.payments.map(payment => (
          <tr key={payment.reference}>
            <th>Payment</th>
            <td>
              <PaymentDetails payment={payment} />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

DetailTable.propTypes = {
  signupWithExternals: Pt.instanceOf(SignupWithExternalsRecord).isRequired,
}



// WEBPACK FOOTER //
// ./src/components/SignupDetails/DetailTable.jsx